import React, { useEffect, useState } from 'react';
import { ArrowRight, Info, Check } from 'lucide-react';
import { Question } from '../types';
import { Button } from './ui/Button';
interface QuizQuestionProps {
  question: Question;
  dogName: string;
  totalQuestions: number;
  currentQuestionIndex: number;
  onAnswer: (answer: string | string[], tags?: string[]) => void;
  onNext: () => void;
  setDogName: (name: string) => void;
}
export function QuizQuestion({
  question,
  dogName,
  totalQuestions,
  currentQuestionIndex,
  onAnswer,
  onNext,
  setDogName
}: QuizQuestionProps) {
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  // Reset state when question changes
  useEffect(() => {
    setInputValue('');
    setSelectedOptions([]);
    setIsAnimating(false);
  }, [question.id]);
  const progress = (currentQuestionIndex + 1) / totalQuestions * 100;
  const handleTextSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    if (question.id === 1) {
      setDogName(inputValue.trim());
    }
    handleNext(inputValue.trim());
  };
  const handleOptionSelect = (label: string, tag?: string) => {
    if (question.type === 'single') {
      setSelectedOptions([label]);
      // Small delay for visual feedback before proceeding
      setTimeout(() => {
        handleNext(label, tag ? [tag] : []);
      }, 300);
    } else {
      // Multi select
      setSelectedOptions(prev => {
        if (prev.includes(label)) return prev.filter(o => o !== label);
        if (label.includes('Ninguna') || label.includes('Nada aún')) return [label]; // Exclusive options
        // If selecting normal option, remove exclusive options
        const withoutExclusive = prev.filter(o => !o.includes('Ninguna') && !o.includes('Nada aún'));
        return [...withoutExclusive, label];
      });
    }
  };
  const handleMultiSubmit = () => {
    if (selectedOptions.length === 0) return;
    // Collect tags from selected options
    const tags = question.options?.filter(opt => selectedOptions.includes(opt.label) && opt.tag).map(opt => opt.tag!) || [];
    handleNext(selectedOptions, tags);
  };
  const handleNext = (answer: string | string[], tags: string[] = []) => {
    setIsAnimating(true);
    onAnswer(answer, tags);
    setTimeout(() => {
      onNext();
    }, 300);
  };
  // Replace [nombre] placeholder
  const formatText = (text: string) => {
    return text.replace(/\[nombre\]/gi, dogName || 'tu perro');
  };
  return <div className={`min-h-screen flex flex-col max-w-md mx-auto px-4 py-6 transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`} data-id="element-123">
      {/* Progress Bar */}
      <div className="w-full h-2 bg-gray-200 rounded-full mb-2" data-id="element-124">
        <div className="h-full bg-primary rounded-full transition-all duration-500 ease-out" style={{
        width: `${progress}%`
      }} data-id="element-125"></div>
      </div>
      <div className="text-xs text-primary font-medium mb-6 flex justify-between" data-id="element-126">
        <span data-id="element-127">
          {question.stepLabel || `Paso ${currentQuestionIndex + 1} de ${totalQuestions}`}
        </span>
        <span data-id="element-128">{Math.round(progress)}%</span>
      </div>

      {/* Question Content */}
      <div className="flex-1 flex flex-col" data-id="element-129">
        {question.hasInfoBox && <div className="bg-primary/10 p-4 rounded-xl mb-6 flex gap-3 items-start" data-id="element-130">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" data-id="element-131" />
            <p className="text-sm text-text-main/80 leading-relaxed" data-id="element-132">
              {formatText(question.headline)}
            </p>
          </div>}

        {!question.hasInfoBox && <h2 className="text-2xl font-bold text-text-main mb-4 leading-tight" data-id="element-133">
            {formatText(question.headline)}
          </h2>}

        {question.subtext && <p className="text-text-main/60 mb-6 font-medium" data-id="element-134">
            {formatText(question.subtext)}
          </p>}

        {/* Input Types */}
        {question.type === 'text' && <form onSubmit={handleTextSubmit} className="mt-4" data-id="element-135">
            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={question.placeholder} className="w-full p-4 text-lg border-2 border-text-main/20 rounded-xl focus:border-primary focus:ring-primary outline-none transition-colors bg-white" autoFocus data-id="element-136" />
            <Button type="submit" fullWidth className="mt-6" disabled={!inputValue.trim()} data-id="element-137">
              Continuar <ArrowRight className="ml-2 w-5 h-5" data-id="element-138" />
            </Button>
          </form>}

        {(question.type === 'single' || question.type === 'multi') && <div className="space-y-3 mt-2" data-id="element-139">
            {question.options?.map((option, idx) => {
          const isSelected = selectedOptions.includes(option.label);
          return <button key={idx} onClick={() => handleOptionSelect(option.label, option.tag)} className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-200 flex items-center justify-between group
                    ${isSelected ? 'bg-primary border-primary text-white shadow-md transform scale-[1.02]' : 'bg-white border-text-main/10 text-text-main hover:border-primary/50 hover:bg-primary/5'}`} data-id="element-140">
                  <span className="font-medium text-lg leading-snug" data-id="element-141">
                    {formatText(option.label)}
                  </span>
                  {question.type === 'multi' && <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors
                      ${isSelected ? 'bg-white border-white' : 'border-text-main/20'}`} data-id="element-142">
                      {isSelected && <Check className="w-4 h-4 text-primary" strokeWidth={3} data-id="element-143" />}
                    </div>}
                </button>;
        })}
          </div>}

        {question.type === 'multi' && <div className="mt-8 sticky bottom-4" data-id="element-144">
            <Button onClick={handleMultiSubmit} fullWidth disabled={selectedOptions.length === 0} className="shadow-xl" data-id="element-145">
              Ver siguiente <ArrowRight className="ml-2 w-5 h-5" data-id="element-146" />
            </Button>
          </div>}
      </div>
    </div>;
}